import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Search, Plus, Minus, Trash2, Phone, Mail, MapPin, X, CheckCircle2, Filter, Pill, Truck } from "lucide-react";
import { toast } from "sonner";
import VenusLogo from "../components/VenusLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `${(Math.round(n) || 0).toLocaleString("fr-FR")} F`;

export default function PublicCatalog() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [cart, setCart] = useState([]); // [{product_id, quantity, ...product}]
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(null);
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", customer_email: "",
    customer_address: "", customer_type: "particulier",
    payment_method: "livraison", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const [lastRefresh, setLastRefresh] = useState(Date.now());

  // Charge le catalogue avec retry automatique et timeout — la prod peut
  // avoir des spikes ponctuels (>10s) qui ne doivent PAS faire disparaître
  // le catalogue déjà affiché à l'utilisateur.
  const loadCatalog = async ({ isInitial = false } = {}) => {
    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const { data } = await axios.get(`${API}/public/${slug}/catalog`, {
          timeout: 25000, // évite de rester bloqué indéfiniment
        });
        setData(data);
        setLastRefresh(Date.now());
        setError("");
        return;
      } catch (e) {
        if (attempt < maxAttempts) {
          // backoff exponentiel : 800ms, 2400ms
          await new Promise((r) => setTimeout(r, 800 * attempt * (attempt + 1) / 2));
          continue;
        }
        // Échec définitif : on n'affiche l'erreur que si on n'a jamais
        // chargé de données — sinon on garde l'affichage précédent (le
        // client continue de voir le catalogue malgré un spike réseau).
        if (isInitial) {
          setError(e.response?.data?.detail || "Catalogue temporairement indisponible — réessayez dans quelques secondes.");
        } else {
          // refresh silencieux échoué → on log juste sans casser l'UX
          console.warn("Refresh catalogue échoué, conservation des données existantes :", e?.message);
        }
      }
    }
  };

  useEffect(() => {
    loadCatalog({ isInitial: true });
    // restore cart from sessionStorage
    try { const s = JSON.parse(sessionStorage.getItem(`cart-${slug}`)); if (s) setCart(s); } catch {}
  }, [slug]);

  // Silent auto-refresh every 60 s — keeps stock + prices in sync while the
  // customer browses. Only runs while the tab is visible to avoid wasted requests.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") loadCatalog({ isInitial: false });
    }, 60_000);
    const onVis = () => { if (document.visibilityState === "visible") loadCatalog({ isInitial: false }); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [slug]);

  useEffect(() => { sessionStorage.setItem(`cart-${slug}`, JSON.stringify(cart)); }, [cart, slug]);

  // Debounce search input — typing feels instant but the heavy filter only runs after 200ms idle
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 200);
    return () => clearTimeout(id);
  }, [search]);
  const [visibleCount, setVisibleCount] = useState(60);
  useEffect(() => { setVisibleCount(60); }, [debouncedSearch, category]);

  // Pre-compute a flat searchable string per product once (when data loads)
  const indexedProducts = useMemo(() => {
    if (!data) return [];
    return data.products.map((p) => ({ p, _s: `${p.name} ${p.sku} ${p.category}`.toLowerCase() }));
  }, [data]);

  const filteredProducts = useMemo(() => {
    let list = indexedProducts;
    if (category !== "all") list = list.filter((x) => x.p.category === category);
    if (debouncedSearch) list = list.filter((x) => x._s.includes(debouncedSearch));
    return list.map((x) => x.p);
  }, [indexedProducts, debouncedSearch, category]);

  const products = useMemo(() => filteredProducts.slice(0, visibleCount), [filteredProducts, visibleCount]);

  // Catégories enrichies : pour chaque catégorie, un compteur + une image
  // représentative (1er produit avec image_url valide) → utilisé par la vitrine
  // "Parcourir par catégorie" en haut de la page.
  const isValidImage = (url) => url && url !== "__retry__";
  const categoryCards = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    for (const p of data.products) {
      const c = p.category || "Autres";
      if (!map.has(c)) {
        map.set(c, { name: c, count: 0, image_url: "" });
      }
      const entry = map.get(c);
      entry.count += 1;
      if (!entry.image_url && isValidImage(p.image_url)) entry.image_url = p.image_url;
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [data]);

  const cartTotal = useMemo(() => cart.reduce((s, it) => s + it.price * it.quantity, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, it) => s + it.quantity, 0), [cart]);

  const addToCart = (p) => {
    setCart((prev) => {
      const ex = prev.find((it) => it.product_id === p.id);
      if (ex) return prev.map((it) => it.product_id === p.id ? { ...it, quantity: it.quantity + 1 } : it);
      return [...prev, { product_id: p.id, quantity: 1, name: p.name, sku: p.sku, price: p.price, unit: p.unit, stock: p.stock }];
    });
    toast.success(`${p.name} ajouté au panier`);
  };

  const updateQty = (pid, delta) => {
    setCart((prev) => prev.map((it) => it.product_id === pid ? { ...it, quantity: Math.max(1, it.quantity + delta) } : it));
  };

  const removeFromCart = (pid) => setCart((prev) => prev.filter((it) => it.product_id !== pid));

  const submitOrder = async (e) => {
    e.preventDefault();
    if (!cart.length) return toast.error("Panier vide");
    if (!form.customer_name || !form.customer_phone) return toast.error("Nom et téléphone requis");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/${slug}/orders`, {
        ...form,
        organization_slug: slug,
        lines: cart.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
      });
      setOrderConfirmed(data);
      setCart([]); sessionStorage.removeItem(`cart-${slug}`);
      // Reset form so the next customer doesn't see previous customer's info
      setForm({
        customer_name: "", customer_phone: "", customer_email: "",
        customer_address: "", customer_type: "particulier",
        payment_method: "livraison", notes: "",
      });
      setShowCheckout(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <VenusLogo size={64} className="mx-auto rounded-2xl mb-4" />
          <h1 className="font-heading text-2xl font-bold mb-2">Catalogue indisponible</h1>
          <p className="text-[var(--text-muted)]">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-[var(--text-muted)] font-data">Chargement du catalogue...</div>;
  }

  if (orderConfirmed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8E8F1] to-white flex items-center justify-center p-6">
        <div className="surface p-8 max-w-md w-full text-center space-y-4">
          <div className="w-16 h-16 bg-[var(--primary,#C2569B)] rounded-full flex items-center justify-center mx-auto shadow-lg">
            <CheckCircle2 size={32} className="text-white" strokeWidth={2.5} />
          </div>
          <h1 className="font-heading text-3xl font-extrabold">Commande envoyée</h1>
          <div className="bg-[var(--primary-soft,#F8E8F1)] p-4 rounded-lg">
            <div className="label-mono mb-1">Numéro de commande</div>
            <div className="font-heading text-2xl font-bold text-[var(--primary-dark,#8E3A6D)]">{orderConfirmed.order_number}</div>
          </div>
          <p className="text-[var(--text-muted)] text-sm leading-relaxed">
            Merci pour votre confiance. Notre équipe va vous recontacter très prochainement pour confirmer la disponibilité, la livraison et le règlement.
          </p>
          <p className="text-[var(--text-muted)] text-xs">Pour préserver la confidentialité, vos coordonnées ne sont pas conservées entre les commandes.</p>
          <button onClick={() => { setOrderConfirmed(null); setShowCart(false); setShowCheckout(false); }} className="btn-primary w-full" data-testid="new-order-btn">Passer une nouvelle commande</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFBFC]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-[var(--border-line)] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <VenusLogo size={36} className="rounded-xl shadow-md shrink-0" />
            <div className="min-w-0">
              <div className="font-heading font-extrabold tracking-tight truncate">VÉNUS<span className="text-[var(--primary,#C2569B)]"> MÉDICAL</span></div>
              <div className="label-mono truncate">{data.organization.description || "Matériels et Consommables Médicaux Dentaires"}</div>
            </div>
          </div>
          <button data-testid="open-cart" onClick={() => setShowCart(true)} className="relative btn-primary flex items-center gap-2">
            <ShoppingCart size={16}/>
            <span className="hidden sm:inline">Panier</span>
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-white text-[var(--primary-dark,#8E3A6D)] text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-[var(--primary,#C2569B)]">{cartCount}</span>
            )}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 py-10 sm:py-16">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="label-mono mb-3 text-[var(--primary-dark,#8E3A6D)]">Matériels & Consommables Médicaux · Sénégal</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">
              Commandez vos<br/>produits <span className="text-[var(--primary,#C2569B)]">en ligne.</span>
            </h1>
            <p className="text-[var(--text-muted)] text-base mt-4 leading-relaxed max-w-md">
              Plus de {data.products.length} références disponibles. Livraison rapide à Dakar et dans toutes les régions.
            </p>
            <div className="grid grid-cols-2 gap-3 mt-6 max-w-md">
              <div className="bg-white border border-[var(--border-line)] rounded-xl p-3 flex items-center gap-2"><Pill size={18} className="text-[var(--primary,#C2569B)]"/><div className="text-sm"><b>{data.products.length}+</b> produits</div></div>
              <div className="bg-white border border-[var(--border-line)] rounded-xl p-3 flex items-center gap-2"><Truck size={18} className="text-[var(--primary,#C2569B)]"/><div className="text-sm">Livraison <b>nationale</b></div></div>
            </div>
          </div>
          <div className="flex items-center justify-center mt-6 lg:mt-0">
            <div className="bg-white rounded-3xl px-6 sm:px-10 pt-5 sm:pt-6 pb-6 sm:pb-7 shadow-[0_8px_40px_-12px_rgba(194,86,155,0.25)] flex flex-col items-center w-full max-w-sm lg:max-w-none">
              <img
                src="/logo512.png"
                alt="Vénus Médical"
                className="w-44 h-44 sm:w-56 sm:h-56 lg:w-[22rem] lg:h-[22rem] object-contain -mb-4 sm:-mb-6"
                draggable={false}
              />
              <div className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight text-[var(--text)] leading-none">
                VÉNUS <span className="text-[var(--primary,#C2569B)]">MÉDICAL</span>
              </div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-[var(--text-muted)] font-medium mt-1.5 text-center">
                Matériel · Consommables · Médicaux
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Catégories en vedette (vitrine) — masquée si une catégorie filtrée est déjà active ou si on recherche */}
      {category === "all" && !debouncedSearch && categoryCards.length > 1 && (
        <section className="max-w-7xl mx-auto px-4 pb-6" data-testid="category-showcase">
          <div className="flex items-end justify-between mb-3">
            <div>
              <h2 className="font-heading text-lg sm:text-xl font-bold text-[var(--text)] tracking-tight">Parcourir par catégorie</h2>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">{categoryCards.length} catégories disponibles</p>
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2.5 sm:gap-3">
            {categoryCards.map((c) => (
              <button
                key={c.name}
                data-testid={`cat-card-${c.name}`}
                onClick={() => {
                  setCategory(c.name);
                  // smooth scroll vers la grille produits
                  setTimeout(() => document.getElementById("product-grid")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
                }}
                className="group surface p-2 sm:p-2.5 hover:border-[var(--primary,#C2569B)] hover:shadow-md transition-all text-left flex flex-col items-center"
              >
                <div className="aspect-square w-full bg-gradient-to-br from-[#F8E8F1] to-white rounded-md flex items-center justify-center mb-2 border border-[var(--border-line)] overflow-hidden">
                  {c.image_url ? (
                    <img src={c.image_url} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                  ) : (
                    <Pill size={28} className="text-[var(--primary,#C2569B)] opacity-50" />
                  )}
                </div>
                <div className="font-semibold text-[11px] sm:text-xs leading-tight text-[var(--text)] line-clamp-2 text-center w-full">{c.name}</div>
                <div className="text-[10px] text-[var(--primary,#C2569B)] font-bold mt-0.5">{c.count} articles</div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Search + filters */}
      <section className="max-w-7xl mx-auto px-4 pb-6">
        <div className="surface p-4 space-y-3">
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input data-testid="search-products" className="input-base pl-11" placeholder="Rechercher un médicament, un nom..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {data.categories.length > 0 && (
            <>
              {/* Mobile: select dropdown */}
              <div className="flex items-center gap-2 sm:hidden">
                <Filter size={14} className="text-[var(--text-muted)] shrink-0" />
                <select
                  data-testid="category-select-mobile"
                  className="input-base flex-1"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="all">Toutes les catégories</option>
                  {data.categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              {/* Desktop: pill buttons */}
              <div className="hidden sm:flex flex-wrap items-center gap-2">
                <Filter size={14} className="text-[var(--text-muted)]" />
                <button onClick={() => setCategory("all")} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${category === "all" ? "bg-[var(--primary,#C2569B)] text-white border-[var(--primary,#C2569B)]" : "bg-white border-[var(--border-line)] text-[var(--text-muted)] hover:border-[var(--primary,#C2569B)]/50"}`}>Toutes</button>
                {data.categories.map((c) => (
                  <button key={c} onClick={() => setCategory(c)} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${category === c ? "bg-[var(--primary,#C2569B)] text-white border-[var(--primary,#C2569B)]" : "bg-white border-[var(--border-line)] text-[var(--text-muted)] hover:border-[var(--primary,#C2569B)]/50"}`}>{c}</button>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Product grid */}
      <section id="product-grid" className="max-w-7xl mx-auto px-4 pb-16">
        {category !== "all" && (
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="flex items-center gap-2">
              <span className="font-heading text-base sm:text-lg font-bold text-[var(--text)]">{category}</span>
              <span className="text-xs text-[var(--text-muted)]">— {filteredProducts.length} articles</span>
            </div>
            <button
              data-testid="back-to-categories"
              onClick={() => { setCategory("all"); setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30); }}
              className="text-xs font-semibold text-[var(--primary,#C2569B)] hover:underline"
            >
              ← Toutes les catégories
            </button>
          </div>
        )}
        {!products.length ? (
          <div className="surface p-10 text-center text-[var(--text-muted)]">Aucun produit</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {products.map((p) => (
              <div key={p.id} className="surface p-4 flex flex-col" data-testid={`product-${p.sku}`}>
                <div className="aspect-square bg-gradient-to-br from-[#F8E8F1] to-white rounded-lg flex items-center justify-center mb-3 border border-[var(--border-line)] overflow-hidden relative">
                  {p.is_new && (
                    <span
                      data-testid={`badge-new-${p.sku}`}
                      className="absolute top-2 left-2 z-10 bg-[var(--primary,#C2569B)] text-white text-[10px] font-extrabold tracking-wider px-2 py-0.5 rounded-full shadow-md uppercase"
                    >
                      Nouveau
                    </span>
                  )}
                  {isValidImage(p.image_url) ? (
                    <img
                      src={p.image_url}
                      alt={p.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        // Fallback to placeholder if the image is missing
                        e.target.style.display = "none";
                        if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div
                    className={`${isValidImage(p.image_url) ? "hidden" : "flex"} w-full h-full items-center justify-center`}
                  >
                    <Pill size={48} className="text-[var(--primary,#C2569B)]/40" strokeWidth={1.5}/>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm leading-tight line-clamp-2">{p.name}</div>
                  <div className="font-data text-xs text-[var(--text-muted)] mt-1">{p.sku}</div>
                  {p.category && <div className="text-xs mt-1.5"><span className="bg-[var(--bg-soft,#f7f7f7)] px-2 py-0.5 rounded-full text-[var(--text-muted)]">{p.category}</span></div>}
                </div>
                <div className="flex items-end justify-between mt-3 pt-3 border-t border-[var(--border-line)]">
                  <div>
                    <div className="font-data text-xs text-[var(--text-muted)] italic">Prix sur demande</div>
                    <div className="text-xs text-[var(--text-muted)]">/ {p.unit}</div>
                  </div>
                  <button
                    data-testid={`add-${p.sku}`}
                    onClick={() => addToCart(p)}
                    disabled={!p.available}
                    aria-label={p.available ? "Ajouter au panier" : "Rupture de stock"}
                    className="btn-primary text-[11px] md:text-xs px-2.5 py-1.5 md:px-3 md:py-2 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed shrink-0 whitespace-nowrap"
                  >
                    <Plus size={11} className="md:hidden"/>
                    <Plus size={12} className="hidden md:block"/>
                    <span>{p.available ? "Ajouter" : "Rupture"}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {filteredProducts.length > visibleCount && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            <div className="text-xs text-[var(--text-muted)]">
              {visibleCount} / {filteredProducts.length} produits affichés
            </div>
            <button
              type="button"
              data-testid="load-more"
              onClick={() => setVisibleCount((n) => n + 60)}
              className="px-5 py-2 rounded-full bg-white border-2 border-[var(--primary,#C2569B)] text-[var(--primary-dark,#8E3A6D)] font-semibold text-sm hover:bg-[var(--primary-soft,#F8E8F1)] transition"
            >
              Afficher 60 de plus
            </button>
            <button
              type="button"
              onClick={() => setVisibleCount(filteredProducts.length)}
              className="text-xs text-[var(--text-muted)] hover:text-[var(--text)] underline"
            >
              Tout afficher ({filteredProducts.length})
            </button>
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-[var(--border-line)] py-8">
        <div className="max-w-7xl mx-auto px-4 space-y-6">
          {/* Brand line */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <VenusLogo size={24} className="rounded-md"/>
              <div className="text-sm font-medium">Vénus Médical · Matériels & Consommables Médicaux Dentaires</div>
            </div>
            <div className="text-xs text-[var(--text-muted)]">© 2026 · Matériels &amp; Consommables Médicaux</div>
          </div>

          {/* Developer signature */}
          <div className="pt-5 border-t border-[var(--border-line)]/60 flex flex-col items-center text-center gap-2">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-medium">
              Application développée par
            </div>
            <div className="font-heading font-bold text-sm text-[var(--text)]">
              Alassane Coundoul
            </div>
            <div className="text-xs text-[var(--text-muted)] max-w-md">
              Applications web sur mesure · SaaS &amp; PWA · Solutions pour PME
            </div>
            <a
              href="https://wa.me/34662424798?text=Bonjour%20Alassane%2C%20je%20viens%20du%20catalogue%20V%C3%A9nus%20M%C3%A9dical%20et%20je%20suis%20int%C3%A9ress%C3%A9%20par%20vos%20services%20de%20d%C3%A9veloppement."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dev-whatsapp"
              className="inline-flex items-center gap-2 mt-1 px-4 py-2 rounded-full bg-[#25D366] text-white text-xs font-semibold shadow-sm hover:brightness-110 transition"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 0 1 8.412 3.488 11.82 11.82 0 0 1 3.48 8.413c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.687-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
              </svg>
              Besoin d'une app ? WhatsApp +34 662 424 798
            </a>
          </div>
        </div>
      </footer>

      {/* Cart drawer */}
      {showCart && (
        <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowCart(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-full sm:w-[420px] bg-white shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="cart-drawer">
            <div className="p-4 border-b border-[var(--border-line)] flex items-center justify-between">
              <div>
                <div className="font-heading font-extrabold text-xl">Mon panier</div>
                <div className="label-mono">{cartCount} article(s)</div>
              </div>
              <button onClick={() => setShowCart(false)} className="text-[var(--text-muted)] hover:text-[var(--text-base)]"><X size={22}/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {!cart.length ? (
                <div className="text-center text-[var(--text-muted)] py-12">
                  <ShoppingCart size={48} className="mx-auto opacity-30 mb-3"/>
                  Votre panier est vide
                </div>
              ) : cart.map((it) => (
                <div key={it.product_id} className="flex gap-3 p-3 border border-[var(--border-line)] rounded-lg">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{it.name}</div>
                    <div className="font-data text-xs text-[var(--text-muted)]">{it.sku}</div>
                    <div className="font-data text-xs text-[var(--text-muted)] italic mt-1">Prix sur demande</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <button onClick={() => removeFromCart(it.product_id)} className="text-[var(--text-muted)] hover:text-[var(--alert,#c32a2a)]"><Trash2 size={14}/></button>
                    <div className="flex items-center gap-1 border border-[var(--border-line)] rounded-lg">
                      <button onClick={() => updateQty(it.product_id, -1)} className="w-7 h-7 flex items-center justify-center hover:bg-[var(--bg-soft,#f7f7f7)]"><Minus size={12}/></button>
                      <span className="font-data font-semibold text-sm w-8 text-center">{it.quantity}</span>
                      <button onClick={() => updateQty(it.product_id, 1)} className="w-7 h-7 flex items-center justify-center hover:bg-[var(--bg-soft,#f7f7f7)]"><Plus size={12}/></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {cart.length > 0 && (
              <div className="p-4 border-t border-[var(--border-line)] space-y-3">
                <div className="text-xs text-[var(--text-muted)] italic text-center">Notre équipe vous contactera pour confirmer le devis et le règlement.</div>
                <button data-testid="checkout-btn" onClick={() => { setShowCart(false); setShowCheckout(true); }} className="btn-primary w-full">Passer commande</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Checkout */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto p-4" onClick={() => setShowCheckout(false)}>
          <form onSubmit={submitOrder} autoComplete="off" className="bg-white rounded-2xl max-w-lg mx-auto my-4 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="checkout-modal">
            <div className="p-5 border-b border-[var(--border-line)] flex items-center justify-between">
              <div>
                <div className="font-heading font-extrabold text-xl">Vos coordonnées</div>
                <div className="label-mono mt-1">{cartCount} article(s) — devis sur demande</div>
              </div>
              <button type="button" onClick={() => setShowCheckout(false)} className="text-[var(--text-muted)] hover:text-[var(--text-base)]"><X size={22}/></button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="label-mono block mb-1.5">Vous êtes</label>
                <select className="input-base" value={form.customer_type} onChange={(e) => setForm({ ...form, customer_type: e.target.value })}>
                  <option value="particulier">Particulier</option>
                  <option value="pharmacie">Pharmacie</option>
                  <option value="dispensaire">Dispensaire</option>
                  <option value="clinique">Clinique</option>
                  <option value="hopital">Hôpital</option>
                </select>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Nom complet *</label>
                <input data-testid="ck-name" required autoComplete="off" name="vm_name" className="input-base" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="ex. Pharmacie de la Plage" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-mono block mb-1.5">Téléphone *</label>
                  <input data-testid="ck-phone" required autoComplete="off" name="vm_phone" className="input-base" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="+221 ..." />
                </div>
                <div>
                  <label className="label-mono block mb-1.5">Email</label>
                  <input type="email" autoComplete="off" name="vm_email" className="input-base" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Adresse de livraison</label>
                <input autoComplete="off" name="vm_addr" className="input-base" value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} placeholder="Quartier, ville" />
              </div>
              <div>
                <label className="label-mono block mb-1.5">Mode de paiement</label>
                <select className="input-base" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                  <option value="livraison">Paiement à la livraison</option>
                  <option value="virement">Virement bancaire</option>
                </select>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Notes (optionnel)</label>
                <textarea rows={2} className="input-base" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Heure de livraison souhaitée, instructions..." />
              </div>
            </div>
            <div className="p-5 border-t border-[var(--border-line)]">
              <button data-testid="submit-public-order" type="submit" disabled={submitting} className="btn-primary w-full">
                {submitting ? "Envoi en cours..." : "Envoyer la commande"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
